<div class="container-fluid complyon-page legal-matters-page px-3 px-md-4 px-xl-4 py-3">
  <div class="legal-matters-layout mx-auto">
    <main class="legal-matters-main">
      <section class="mb-3">
        <h1 class="complyon-title legal-matters-title mb-2 text-l">Legal matters & litigation</h1>
        <p class="complyon-subtitle legal-matters-subtitle mb-0 text-s">
          Legal cases, disputes, regulatory notices, claims and opinions — each linked to the contract, supplier, employee, incident, risk and evidence it touches, with key dates and matter status.
        </p>
      </section>

      <section class="row g-3 mb-3">
        <div v-for="card in cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card legal-matters-stat-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value text-l" :class="card.accent === 'red' ? 'text-danger' : card.accent === 'amber' ? 'text-warning-emphasis' : 'text-dark'">
              {{ card.value }}
            </div>
            <div class="complyon-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel legal-matters-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 pt-3 pb-2">
          <div class="min-w-0">
            <div class="complyon-section-title text-s">Legal matters & litigation</div>
            <div class="complyon-section-subtitle text-xs">
              Legal cases, disputes, regulatory notices, claims and opinions — each with its parties, responsible attorney, key dates, risk rating and the contract, supplier, employee, incident and evidence it connects to.
            </div>
          </div>
          <button type="button" class="btn config-navbar-primary-action legal-matters-log-btn flex-shrink-0">
            <i class="bi bi-plus-lg"></i>
            <span>Log matter</span>
          </button>
        </div>

        <div class="legal-matters-toolbar px-3 pb-2">
          <div class="d-flex flex-wrap align-items-center gap-2">
            <button
              v-for="filter in filters"
              :key="filter.id"
              type="button"
              class="btn legal-matters-filter-chip"
              :class="{ active: active_filter === filter.id }"
              @click="set_filter(filter.id)"
            >
              <span>{{ filter.label }}</span>
              <span>{{ filter.count }}</span>
            </button>
          </div>

          <div class="legal-matters-search-wrap">
            <i class="bi bi-search"></i>
            <input
              v-model="search_query"
              type="text"
              class="form-control legal-matters-search"
              placeholder="Search..."
            />
          </div>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table legal-matters-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Matter</th>
                <th class="text-xs">Case type</th>
                <th class="text-xs">Parties</th>
                <th class="text-xs">Attorney</th>
                <th class="text-xs">Risk</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in filtered_rows"
                :key="row.ref"
                class="legal-matters-row"
                :class="{ 'is-active': active_matter_ref === row.ref }"
                tabindex="0"
                @click="open_matter_drawer(row)"
                @keydown.enter.prevent="open_matter_drawer(row)"
                @keydown.space.prevent="open_matter_drawer(row)"
              >
                <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
                <td class="fw-semibold legal-matters-matter">{{ row.matter }}</td>
                <td>{{ row.case_type }}</td>
                <td>{{ row.parties }}</td>
                <td>{{ row.attorney }}</td>
                <td>
                  <span
                    class="badge rounded-pill"
                    :class="row.risk === 'High' ? 'bg-danger-subtle text-danger-emphasis' : row.risk === 'Medium' ? 'bg-warning-subtle text-warning-emphasis' : 'bg-success-subtle text-success-emphasis'"
                  >
                    {{ row.risk }}
                  </span>
                </td>
                <td><span class="badge complyon-badge" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </main>

    <aside class="legal-matters-ai d-none">
      <div class="legal-matters-ai-panel">
        <div class="legal-matters-ai-header">
          <div class="d-flex align-items-center gap-2 min-w-0">
            <span class="legal-matters-ai-icon">
              <i class="bi bi-stars"></i>
            </span>
            <div class="legal-matters-ai-title">Complyon AI</div>
          </div>
          <span class="badge rounded-pill legal-matters-live-pill">LIVE</span>
        </div>

        <div class="legal-matters-ai-section">
          <div class="legal-matters-ai-kicker">Briefing</div>
          <div class="legal-matters-ai-copy">{{ ai_briefing.title }}</div>
          <div class="legal-matters-brief-card">
            <p v-for="paragraph in ai_briefing.body" :key="paragraph" class="legal-matters-brief-line mb-0">
              {{ paragraph }}
            </p>
            <div class="legal-matters-brief-footer">{{ ai_briefing.footer }}</div>
          </div>
        </div>

        <div class="legal-matters-ai-section">
          <div class="legal-matters-ai-kicker">Ask compliance</div>
          <div class="d-grid gap-2">
            <button
              v-for="question in ai_questions"
              :key="question"
              type="button"
              class="btn legal-matters-question-btn text-start"
            >
              <i class="bi bi-record-circle"></i>
              <span>{{ question }}</span>
            </button>
          </div>
        </div>
      </div>
    </aside>
  </div>

  <teleport to="body">
    <div
      v-if="active_matter && active_matter_drawer"
      class="command-centre-drawer-backdrop"
      @click.self="close_matter_drawer"
    ></div>

    <aside
      v-if="active_matter && active_matter_drawer"
      class="command-centre-drawer legal-matters-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Legal matter drawer"
    >
      <header class="command-centre-drawer-header legal-matters-drawer-header border-bottom">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div class="min-w-0">
            <div class="legal-matters-drawer-kicker">
              {{ String(active_matter_drawer.kicker || 'Legal matter').toUpperCase() }} · {{ active_matter.ref }}
            </div>
            <div class="legal-matters-drawer-title mt-1">{{ active_matter.matter }}</div>
            <div class="legal-matters-drawer-subtitle mt-1">{{ active_matter_drawer.subtitle }}</div>
          </div>
          <div class="d-flex align-items-start gap-2 flex-shrink-0">
            <span class="badge rounded-pill legal-matters-status-pill" :class="active_matter_drawer.badge.class">
              {{ active_matter_drawer.badge.label }}
            </span>
            <button type="button" class="btn btn-link p-0 command-centre-drawer-close" @click="close_matter_drawer" aria-label="Close drawer">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
      </header>

      <div class="command-centre-drawer-body legal-matters-drawer-body">
        <section class="legal-matters-drawer-details">
          <div v-for="item in active_matter_drawer.details" :key="`${active_matter.ref}-${item.label}`" class="legal-matters-drawer-detail-row">
            <div class="legal-matters-drawer-detail-label">{{ item.label }}</div>
            <div class="legal-matters-drawer-detail-value">{{ item.value }}</div>
          </div>
        </section>

        <section v-if="active_matter_drawer.evidence_checks.length" class="legal-matters-drawer-section">
          <div class="d-flex align-items-center justify-content-between gap-2">
            <div class="legal-matters-drawer-section-title">{{ active_matter_drawer.evidence_checks_title }}</div>
            <span v-if="active_matter_drawer.evidence_checks_badge" class="badge rounded-pill legal-matters-section-pill bg-warning-subtle text-warning-emphasis">
              {{ active_matter_drawer.evidence_checks_badge }}
            </span>
          </div>
          <div class="d-grid gap-2 mt-2">
            <article v-for="item in active_matter_drawer.evidence_checks" :key="`${active_matter.ref}-${item.title}`" class="legal-matters-check-card">
              <div class="d-flex align-items-start justify-content-between gap-2">
                <div class="d-flex align-items-start gap-2 min-w-0">
                  <span class="legal-matters-check-icon" :class="legal_matter_check_state_class(item.state)">
                    <i class="bi" :class="legal_matter_check_icon(item.state)"></i>
                  </span>
                  <div class="min-w-0">
                    <div class="legal-matters-check-title">{{ item.title }}</div>
                    <div class="legal-matters-check-subtitle">{{ item.subtitle }}</div>
                  </div>
                </div>
                <span class="badge rounded-pill legal-matters-check-pill" :class="legal_matter_check_badge_class(item.state)">
                  {{ item.state_label }}
                </span>
              </div>
            </article>
          </div>
        </section>

        <section class="legal-matters-drawer-section">
          <div class="legal-matters-drawer-section-title">Connected records</div>
          <div class="legal-matters-connected-grid mt-2">
            <article v-for="record in active_matter_drawer.connected_records" :key="`${active_matter.ref}-${record.type}-${record.title}`" class="legal-matters-connected-card">
              <div class="d-flex align-items-start gap-2">
                <div class="legal-matters-connected-icon">
                  <i class="bi" :class="legal_matter_record_icon(record.type)"></i>
                </div>
                <div class="min-w-0 flex-grow-1">
                  <div class="legal-matters-connected-type">{{ record.type }}</div>
                  <div class="legal-matters-connected-title">{{ record.title }}</div>
                </div>
                <i class="bi bi-box-arrow-up-right legal-matters-connected-link"></i>
              </div>
            </article>
          </div>
        </section>

        <section class="legal-matters-drawer-section">
          <div class="d-flex align-items-center justify-content-between gap-2">
            <div class="legal-matters-drawer-section-title">Recommended action</div>
            <span class="badge rounded-pill legal-matters-ai-mini-pill">AI</span>
          </div>
          <div class="legal-matters-recommendation-card mt-2">
            {{ active_matter_drawer.recommendation }}
          </div>
        </section>
      </div>

      <footer class="command-centre-drawer-footer legal-matters-drawer-footer border-top">
        <div class="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <button type="button" class="btn btn-link text-decoration-none legal-matters-drawer-link">
            <i class="bi bi-flag me-1"></i>
            {{ active_matter_drawer.footer.left_label }}
          </button>
          <div class="d-flex align-items-center gap-2">
            <button type="button" class="btn btn-outline-secondary legal-matters-drawer-secondary">
              <i class="bi bi-folder2-open me-1"></i>
              {{ active_matter_drawer.footer.secondary_label }}
            </button>
            <button type="button" class="btn config-navbar-primary-action legal-matters-drawer-primary">
              <i class="bi bi-arrow-right-short me-1"></i>
              {{ active_matter_drawer.footer.primary_label }}
            </button>
          </div>
        </div>
      </footer>
    </aside>
  </teleport>
</div>
